import { useState } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid ${p => p.theme.form.border};
    flex-shrink: 0;
`

const Title = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 0;
`

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.5rem;
`

const Note = styled.p`
    font-size: 0.85rem;
    color: ${p => p.theme.font.label};
    margin: 0 0 0.75rem;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`

const Th = styled.th`
    text-align: left;
    padding: 0.5rem 0.75rem;
    color: ${p => p.theme.font.label};
    font-weight: 600;
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const Td = styled.td`
    padding: 0.4rem 0.75rem;
    color: ${p => p.theme.font.color};
    border-bottom: 1px solid ${p => p.theme.form.border};
    vertical-align: top;
`

const Text = styled.div`
    color: ${p => p.theme.font.label};
    font-size: 0.8rem;
`

const Outcome = styled.span`
    font-size: 0.85rem;
    color: ${p => p.$error ? p.theme.emotive.error.color : p.theme.font.color};
`

const Action = styled.button`
    background: ${p => p.theme.btn.primary};
    border: none;
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0.4rem 0.9rem;
    white-space: nowrap;

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`

/*
 * Admin › Periodic › Statistics: rebuild the organization's precomputed stats
 * (the figures behind the overview widgets) from all of its data.
 *
 * Booking keeps a stat current as it goes; a rebuild is for data from before a
 * stat existed, or a stat that has drifted. The server rebuilds the chosen
 * stats in its own order (a stat made from another after it) and answers once
 * all are done, with what each one wrote.
 */
const StatsPage = () => {
    const { t, m } = useTranslation()
    const { getMe, postMe } = useApi()
    const [selected, setSelected] = useState([])
    const [running, setRunning] = useState(false)
    const [results, setResults] = useState({})
    const [failure, setFailure] = useState(null)

    const { data: definitions, isLoading } = useQuery({
        queryKey: ['stats', 'definitions'],
        queryFn: () => getMe('stats/definitions')
    })

    const stats = Array.isArray(definitions?.data) ? definitions.data : []
    const allSelected = stats.length > 0 && selected.length === stats.length

    const toggle = (key) => setSelected(current =>
        current.includes(key) ? current.filter(k => k !== key) : [...current, key])

    const toggleAll = () => setSelected(allSelected ? [] : stats.map(s => s.key))

    const run = async () => {
        setRunning(true)
        setFailure(null)
        setResults({})
        try {
            const response = await postMe('stats/regenerate', { stats: selected })
            if (Array.isArray(response?.data)) {
                setResults(Object.fromEntries(response.data.map(r => [r.key, r])))
            } else {
                setFailure(response?.error ?? 'admin.stats.error.failed')
            }
        } finally {
            setRunning(false)
        }
    }

    const outcome = (key) => {
        const result = results[key]
        if (!result) return running && selected.includes(key) ? <Outcome>{t('admin.stats.result.running')}</Outcome> : null
        if (result.error) return <Outcome $error>{result.error}</Outcome>
        if (result.skipped) return <Outcome>{t('admin.stats.result.skipped')}</Outcome>
        return <Outcome>{m('admin.stats.result.stored', [result.stored])}</Outcome>
    }

    return (
        <PageWrap>
            <Header>
                <Title>{t('admin.stats.title')}</Title>
                <Action data-testid='admin-stats-run' disabled={running || selected.length === 0} onClick={run}>
                    {running ? t('admin.stats.action.running') : t('admin.stats.action.run')}
                </Action>
            </Header>
            <Body>
                <Note>{t('admin.stats.note')}</Note>
                {failure && <Note><Outcome $error>{t(failure)}</Outcome></Note>}
                {isLoading ? null : (
                    <Table>
                        <thead>
                            <tr>
                                <Th style={{ width: '2rem' }}>
                                    <input type='checkbox' checked={allSelected} disabled={running} onChange={toggleAll}
                                        aria-label={t('admin.stats.table.all')} data-testid='admin-stats-all' />
                                </Th>
                                <Th>{t('admin.stats.table.stat')}</Th>
                                <Th>{t('admin.stats.table.result')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.map(stat => (
                                <tr key={stat.key}>
                                    <Td>
                                        <input type='checkbox' checked={selected.includes(stat.key)} disabled={running}
                                            onChange={() => toggle(stat.key)} data-testid={`admin-stats-${stat.key}`} />
                                    </Td>
                                    <Td>
                                        <div>{t(`admin.stats.stat.${stat.key}`)}</div>
                                        <Text>{t(`admin.stats.stat.${stat.key}.text`)}</Text>
                                    </Td>
                                    <Td>{outcome(stat.key)}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Body>
        </PageWrap>
    )
}

export default StatsPage
